/**
 * Partnerske ponude jedne firme (ADR-071): stanje programa, način nagrade,
 * stanja, knjiga, isplate i prodaje.
 *
 * Stanje se ne čuva kao kolona - izvodi se iz događaja (samo dopisivanje), pa
 * kartica prikazuje i celu istoriju: ko je zatražio, ko odlučio i zašto.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Banknote, Check, Link2, Plus, RefreshCw, Save } from 'lucide-react';
import { api } from '../../../api/client';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { OdlukaOZahtevu, UkljucivanjeDijalog } from '../../affiliate/AffiliatePage';
import {
  type ConversionView, type EnrollmentStatus, type IsplataRadnja, type OfferView, type PayoutView, type RewardMode,
  type Valuta, Dijalog, DugmadDijaloga, IsplataPotvrda, IsplateTabela, IzborNacina, NACIN_NAGRADE, Oznaka, Polje,
  ProdajeTabela, STATUS_PROGRAMA, VALUTE, btnDangerSecondary, btnPrimary, btnSecondary, card, errorBox, input, mono,
  mozeDaMenja, napomena, okBox, porukaGreske, prazno, rsd, tabela, td, th, trenutak, datum,
} from '../../affiliate/shared';

type EventKind = 'requested' | 'approved' | 'rejected' | 'activated' | 'deactivated' | 'reward_mode_set';
type LedgerKind = 'applied_to_billing' | 'adjustment' | 'paid_out';

type Stanje = {
  tenant: { id: string; name: string; isFlatRate: boolean; suspended: boolean };
  status: EnrollmentStatus;
  reason: string | null;
  requestedAt: string | null;
  decidedAt: string | null;
  /** `chosen: null` = firma nikad nije dobila izbor (važe krediti). */
  rewardMode: { effective: RewardMode; chosen: RewardMode | null };
  tenantLinks: TenantLink[];
  terms: { id: string; version: number; accepted: boolean; acceptedAt: string | null } | null;
  balances: { creditRsd: string; cashAvailableRsd: string; cashPendingRsd: string; pendingShareRsd: string };
  events: Array<{ id: string; kind: EventKind; rewardMode: RewardMode | null; reason: string | null; actorUserId: string | null; actorSysadminId: string | null; createdAt: string }>;
  ledger: Array<{ id: string; kind: LedgerKind; amountRsd: string; billingInvoiceId: string | null; billingPeriodStart: string | null; payoutRequestId: string | null; reason: string | null; sysadminId: string | null; createdAt: string }>;
  payouts: PayoutView[];
  conversions: ConversionView[];
};

type TenantLink = { offerId: string; offerTitle: string; offerActive: boolean; networkName: string; url: string | null; updatedAt: string | null };

const DOGADJAJ: Record<EventKind, string> = {
  requested: 'Zatražen pristup',
  approved: 'Zahtev prihvaćen',
  rejected: 'Zahtev odbijen',
  activated: 'Program uključen',
  deactivated: 'Program isključen',
  reward_mode_set: 'Promenjen način nagrade',
};

const KNJIGA: Record<LedgerKind, string> = {
  applied_to_billing: 'Umanjenje pretplate',
  adjustment: 'Ručna korekcija',
  paid_out: 'Isplata u novcu',
};

type Radnja = 'approve' | 'reject' | 'activate' | 'deactivate' | 'adjust' | 'earning' | 'payout';

export function AffiliateTab({ tenantId }: { tenantId: string }) {
  const admin = mozeDaMenja();
  const [stanje, setStanje] = useState<Stanje | null>(null);
  const [greska, setGreska] = useState<string | null>(null);
  const [poruka, setPoruka] = useState<string | null>(null);
  const [radnja, setRadnja] = useState<Radnja | null>(null);
  const [isplata, setIsplata] = useState<IsplataRadnja | null>(null);
  const [menjaNacin, setMenjaNacin] = useState(false);
  const [ponistava, setPonistava] = useState<ConversionView | null>(null);

  const osvezi = useCallback(async () => {
    setGreska(null);
    try {
      setStanje(await api.get<Stanje>(`/admin/tenants/${tenantId}/affiliate`));
    } catch (e) {
      setGreska(porukaGreske(e, 'Učitavanje nije uspelo.'));
    }
  }, [tenantId]);

  useEffect(() => { void osvezi(); }, [osvezi]);

  const gotovo = (p: string) => { setPoruka(p); void osvezi(); };

  const postaviNacin = async (mode: RewardMode) => {
    setMenjaNacin(true);
    setGreska(null);
    setPoruka(null);
    try {
      const r = await api.put<{ rewardMode: Stanje['rewardMode'] }>(`/admin/tenants/${tenantId}/affiliate/reward-mode`, { rewardMode: mode });
      setPoruka(`Način nagrade: ${NACIN_NAGRADE[r.rewardMode.effective]}. Važi za prodaje koje mreža odobri od sada.`);
      await osvezi();
    } catch (e) {
      setGreska(porukaGreske(e));
    } finally {
      setMenjaNacin(false);
    }
  };

  if (!stanje) {
    if (greska) return <div style={errorBox}><AlertTriangle size={16} /> {greska}</div>;
    return <div style={{ color: 'var(--color-text-muted)' }}>Učitavanje...</div>;
  }

  const st = STATUS_PROGRAMA[stanje.status];
  const { rewardMode: rm, balances: b } = stanje;
  const kredit = Number(b.creditRsd);

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {greska && <div style={{ ...errorBox, marginBottom: 0 }}><AlertTriangle size={16} /> {greska}</div>}
      {poruka && <div style={{ ...okBox, marginBottom: 0 }}><Check size={16} /> {poruka}</div>}

      <div style={{ fontSize: 13, color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
        Lokal pokazuje kupcima QR kodove ponuda partnera i dobija deo provizije kad kupac kupi - kao popust na
        pretplatu (krediti) ili u novcu, samo iz provizije koju je mreža stvarno platila. Ovde uključuješ program
        firmi, biraš način nagrade i pratiš njenu zaradu.{' '}
        <Link to="/affiliate" style={{ color: 'var(--color-primary)' }}>Kako radi →</Link>
      </div>

      {/* Stanje programa */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--color-text-strong)' }}>Partnerske ponude</h3>
              <Oznaka ton={st.ton}>{st.label}</Oznaka>
            </div>
            <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 6, lineHeight: 1.6 }}>
              {stanje.requestedAt && <div>Zatraženo: {trenutak(stanje.requestedAt)}</div>}
              {stanje.decidedAt && <div>Odlučeno: {trenutak(stanje.decidedAt)}</div>}
              {stanje.reason && <div>Razlog: {stanje.reason}</div>}
              <div>
                Uslovi programa:{' '}
                {!stanje.terms ? 'nisu objavljeni'
                  : stanje.terms.accepted ? `v${stanje.terms.version} prihvaćeni ${trenutak(stanje.terms.acceptedAt)}`
                    : `v${stanje.terms.version} - firma ih još nije prihvatila`}
              </div>
              {stanje.tenant.suspended && <div style={{ color: '#fca5a5' }}>Nalog je suspendovan - QR kodovi firme ne rade dok je suspenzija aktivna.</div>}
            </div>
          </div>
          <button onClick={() => void osvezi()} style={btnSecondary}><RefreshCw size={16} /> Osveži</button>
        </div>

        {admin && (
          <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
            {stanje.status === 'pending' && (
              <>
                <button onClick={() => setRadnja('approve')} style={btnPrimary} data-testid="affiliate-prihvati">Prihvati zahtev</button>
                <button onClick={() => setRadnja('reject')} style={btnDangerSecondary}>Odbij zahtev</button>
              </>
            )}
            {stanje.status !== 'active' && stanje.status !== 'pending' && (
              <button onClick={() => setRadnja('activate')} style={btnPrimary} data-testid="affiliate-aktiviraj">Aktiviraj program</button>
            )}
            {stanje.status === 'active' && (
              <button onClick={() => setRadnja('deactivate')} style={btnDangerSecondary} data-testid="affiliate-deaktiviraj">Deaktiviraj program</button>
            )}
          </div>
        )}
      </div>

      {/* Način nagrade */}
      <div style={card}>
        <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, color: 'var(--color-text-strong)' }}>Način nagrade</h3>
        <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--color-text-muted)' }}>
          Važi sada: <strong style={{ color: 'var(--color-text-strong)' }}>{NACIN_NAGRADE[rm.effective]}</strong>
          {rm.chosen === null && !stanje.tenant.isFlatRate ? ' (nije izabrano - važe krediti)' : ''}.
          Bira se za svaku firmu posebno. Način se snima na prodaji u trenutku kad je mreža odobri - promena ne dira
          već odobrene prodaje.
        </p>
        <IzborNacina
          vrednost={stanje.tenant.isFlatRate ? 'credits' : rm.chosen}
          onIzbor={(m) => void postaviNacin(m)}
          pausalac={stanje.tenant.isFlatRate}
          onemoguceno={!admin || menjaNacin}
        />
      </div>

      {/* Linkovi po lokalu (ADR-078) */}
      {stanje.tenantLinks.length > 0 && (
        <LinkoviZaPonude tenantId={tenantId} linkovi={stanje.tenantLinks} admin={admin} onGotovo={gotovo} onGreska={setGreska} />
      )}

      {/* Zarada mimo potvrda mreže */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, color: 'var(--color-text-strong)' }}>Ručni unos zarade i isplate</h3>
            <p style={{ margin: 0, fontSize: 12, color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
              <strong>Dodaj zaradu</strong> - za partnere bez potvrda (link po lokalu): unesite proviziju koju je partner platio
              Efikasi za ovaj lokal, sa referencom uplate; lokal dobija procenat ponude kao kredit ili novac.{' '}
              <strong>Upiši isplatu</strong> - novac koji je lokalu već isplaćen van sistema; umanjuje raspoloživo za isplatu.
            </p>
          </div>
          {admin && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={() => setRadnja('earning')} style={btnPrimary} data-testid="affiliate-dodaj-zaradu"><Plus size={16} /> Dodaj zaradu</button>
              <button onClick={() => setRadnja('payout')} style={btnSecondary} data-testid="affiliate-upisi-isplatu"><Banknote size={16} /> Upiši isplatu</button>
            </div>
          )}
        </div>
      </div>

      {/* Stanja */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))', gap: 12 }}>
        <Iznos naslov="Kredit za pretplatu" vrednost={rsd(b.creditRsd)} opis={kredit < 0 ? 'U minusu - prebija se sa budućom zaradom, ne naplaćuje se.' : 'Umanjuje sledeći račun za pretplatu.'} istaknuto={kredit < 0 ? 'danger' : 'ok'} />
        <Iznos naslov="Raspoloživo za isplatu" vrednost={rsd(b.cashAvailableRsd)} opis="Samo prodaje koje je mreža platila, umanjeno za isplate i zahteve na čekanju." />
        <Iznos naslov="Zahtevi na čekanju" vrednost={rsd(b.cashPendingRsd)} opis="Zatražene i odobrene isplate koje još nisu plaćene." />
        <Iznos naslov="Čeka potvrdu mreže" vrednost={rsd(b.pendingShareRsd)} opis="Deo lokala za prodaje koje mreža još nije odobrila - informativno." />
      </div>

      {/* Knjiga */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--color-text-strong)' }}>Knjiga nagrada ({stanje.ledger.length})</h3>
          <div style={{ flex: 1 }} />
          {admin && <button onClick={() => setRadnja('adjust')} style={btnSecondary}><Plus size={16} /> Ručna korekcija</button>}
        </div>
        {stanje.ledger.length === 0 ? <div style={prazno}>Nema upisa.</div> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={tabela}>
              <thead>
                <tr>
                  <th style={th}>Vreme</th>
                  <th style={th}>Vrsta</th>
                  <th style={{ ...th, textAlign: 'right' }}>Iznos</th>
                  <th style={th}>Period</th>
                  <th style={th}>Opis</th>
                </tr>
              </thead>
              <tbody>
                {stanje.ledger.map((l) => (
                  <tr key={l.id}>
                    <td style={td}>{trenutak(l.createdAt)}</td>
                    <td style={td}>{KNJIGA[l.kind] ?? l.kind}</td>
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap', color: Number(l.amountRsd) < 0 ? '#fca5a5' : '#86efac' }}>{rsd(l.amountRsd)}</td>
                    <td style={td}>{l.billingPeriodStart ? datum(l.billingPeriodStart) : '-'}</td>
                    <td style={td}>{l.reason || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Isplate */}
      <div style={card}>
        <h3 style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 600, color: 'var(--color-text-strong)' }}>Isplate ({stanje.payouts.length})</h3>
        <IsplateTabela isplate={stanje.payouts} prikaziFirmu={false} onRadnja={admin ? setIsplata : undefined} />
      </div>

      {/* Prodaje */}
      <div style={card}>
        <h3 style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 600, color: 'var(--color-text-strong)' }}>
          Prodaje ({stanje.conversions.length}{stanje.conversions.length >= 100 ? ', poslednjih 100' : ''})
        </h3>
        <ProdajeTabela prodaje={stanje.conversions} prikaziFirmu={false} onPonisti={admin ? setPonistava : undefined} />
      </div>

      {/* Istorija */}
      <div style={card}>
        <h3 style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 600, color: 'var(--color-text-strong)' }}>Istorija programa</h3>
        {stanje.events.length === 0 ? <div style={prazno}>Firma nikad nije bila u programu.</div> : (
          <ol style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 6, fontSize: 13 }}>
            {stanje.events.map((e) => (
              <li key={e.id}>
                <strong>{trenutak(e.createdAt)}</strong> - {DOGADJAJ[e.kind] ?? e.kind}
                {(e.kind === 'reward_mode_set' || ((e.kind === 'approved' || e.kind === 'activated') && e.rewardMode))
                  && `${e.kind === 'reward_mode_set' ? ':' : ', nagrada:'} ${e.rewardMode ? NACIN_NAGRADE[e.rewardMode] : 'krediti'}`}
                {' · '}{e.actorSysadminId ? 'administrator platforme' : e.actorUserId ? 'korisnik firme' : 'sistem'}
                {e.reason && <span style={{ color: 'var(--color-text-muted)' }}> · {e.kind === 'requested' ? 'napomena' : 'razlog'}: {e.reason}</span>}
              </li>
            ))}
          </ol>
        )}
      </div>

      {(radnja === 'approve' || radnja === 'reject') && (
        <OdlukaOZahtevu tenantId={tenantId} tenantName={stanje.tenant.name} isFlatRate={stanje.tenant.isFlatRate} vrsta={radnja} onZatvori={() => setRadnja(null)} onGotovo={gotovo} />
      )}

      {radnja === 'activate' && (
        <UkljucivanjeDijalog
          tenantId={tenantId}
          tenantName={stanje.tenant.name}
          isFlatRate={stanje.tenant.isFlatRate}
          vrsta="activate"
          pocetniNacin={rm.chosen}
          onZatvori={() => setRadnja(null)}
          onGotovo={gotovo}
        />
      )}

      {radnja === 'earning' && (
        <DodajZaradu tenantId={tenantId} nacin={rm.effective} onZatvori={() => setRadnja(null)} onGotovo={(p) => { setRadnja(null); gotovo(p); }} />
      )}

      {radnja === 'payout' && (
        <UpisiIsplatu tenantId={tenantId} raspolozivo={b.cashAvailableRsd} onZatvori={() => setRadnja(null)} onGotovo={(p) => { setRadnja(null); gotovo(p); }} />
      )}

      {ponistava && (
        <ConfirmDialog
          title="Poništi ručno unetu zaradu"
          message={`Poništiti zaradu ${rsd(ponistava.tenantShareRsd)} (${ponistava.offerTitle ?? 'ponuda'})? Upis ostaje u istoriji kao poništen. Ako je kredit već iskorišćen ili novac isplaćen, stanje lokala ide u minus i prebija se sa budućom zaradom.`}
          confirmLabel="Poništi"
          input={{ label: 'Razlog (obavezno)', required: true, multiline: true }}
          onConfirm={async (razlog) => {
            try {
              await api.post(`/admin/tenants/${tenantId}/affiliate/earnings/${encodeURIComponent(ponistava.externalTxId)}/reverse`, { reason: (razlog ?? '').trim() });
            } catch (e) {
              throw new Error(porukaGreske(e));
            }
            gotovo('Ručno uneta zarada je poništena.');
          }}
          onClose={() => setPonistava(null)}
        />
      )}

      {radnja === 'deactivate' && (
        <ConfirmDialog
          title="Deaktiviraj program"
          message={`Isključiti partnerske ponude za „${stanje.tenant.name}"? Već prikazani QR kodovi prestaju da rade, a već zarađeno ostaje firmi.`}
          confirmLabel="Deaktiviraj"
          input={{ label: 'Razlog (obavezno, firma ga vidi)', required: true, multiline: true }}
          onConfirm={async (razlog) => {
            try {
              await api.post(`/admin/tenants/${tenantId}/affiliate/deactivate`, { reason: (razlog ?? '').trim() });
            } catch (e) {
              throw new Error(porukaGreske(e));
            }
            gotovo('Program je isključen.');
          }}
          onClose={() => setRadnja(null)}
        />
      )}

      {radnja === 'adjust' && (
        <Korekcija tenantId={tenantId} onZatvori={() => setRadnja(null)} onGotovo={(p) => { setRadnja(null); gotovo(p); }} />
      )}

      {isplata && <IsplataPotvrda radnja={isplata} onZatvori={() => setIsplata(null)} onGotovo={gotovo} />}
    </div>
  );
}

function Iznos({ naslov, vrednost, opis, istaknuto }: { naslov: string; vrednost: string; opis: string; istaknuto?: 'ok' | 'danger' }) {
  return (
    <div style={card}>
      <div style={{ fontSize: 11, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{naslov}</div>
      <div style={{ fontSize: 20, fontWeight: 700, marginTop: 4, color: istaknuto === 'danger' ? '#fca5a5' : istaknuto === 'ok' ? '#86efac' : 'var(--color-text-strong)' }}>{vrednost}</div>
      <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4, lineHeight: 1.4 }}>{opis}</div>
    </div>
  );
}

function Korekcija({ tenantId, onZatvori, onGotovo }: { tenantId: string; onZatvori: () => void; onGotovo: (poruka: string) => void }) {
  const [iznos, setIznos] = useState('');
  const [razlog, setRazlog] = useState('');
  const [greska, setGreska] = useState<string | null>(null);
  const [radi, setRadi] = useState(false);

  const normalizovano = iznos.trim().replace(',', '.');
  const ispravno = /^-?\d+(\.\d{1,2})?$/.test(normalizovano) && Number(normalizovano) !== 0;
  const spremno = ispravno && !!razlog.trim();

  const sacuvaj = async () => {
    if (!spremno) return;
    setRadi(true);
    setGreska(null);
    try {
      const r = await api.post<{ creditRsd: string }>(`/admin/tenants/${tenantId}/affiliate/adjustments`, { amountRsd: normalizovano, reason: razlog.trim() });
      onGotovo(`Korekcija je upisana. Kredit za pretplatu sada: ${rsd(r.creditRsd)}.`);
    } catch (e) {
      setGreska(porukaGreske(e, 'Korekcija nije upisana.'));
    } finally {
      setRadi(false);
    }
  };

  return (
    <Dijalog naslov="Ručna korekcija kredita" onZatvori={onZatvori} sirina={480}>
      {greska && <div style={errorBox}><AlertTriangle size={16} /> {greska}</div>}
      <Polje label="Iznos (RSD)" pomoc="Pozitivan iznos dodaje kredit, negativan (npr. -500) oduzima.">
        <input
          value={iznos}
          onChange={(e) => setIznos(e.target.value)}
          inputMode="decimal"
          placeholder="npr. -500,00"
          style={{ ...input, borderColor: iznos && !ispravno ? '#ef4444' : 'var(--color-border)' }}
          data-testid="affiliate-korekcija-iznos"
        />
      </Polje>
      <Polje label="Razlog (obavezno)">
        <textarea value={razlog} onChange={(e) => setRazlog(e.target.value)} rows={3} maxLength={500} style={{ ...input, fontFamily: 'inherit', resize: 'vertical' }} />
      </Polje>
      <div style={napomena}>Upis u knjigu se ne briše; greška se ispravlja novom korekcijom suprotnog znaka.</div>
      <DugmadDijaloga onOtkazi={onZatvori} onPotvrdi={() => void sacuvaj()} radi={radi} onemoguceno={!spremno} />
    </Dijalog>
  );
}

/**
 * Link partnera za ovu firmu, po ponudi vrste „poseban link za svaki lokal"
 * (ADR-078). Bez linka lokal ponudu ne vidi. Promena linka važi i za već
 * odštampane QR kodove - oni vode preko našeg kratkog linka.
 */
function LinkoviZaPonude({ tenantId, linkovi, admin, onGotovo, onGreska }: {
  tenantId: string; linkovi: TenantLink[]; admin: boolean; onGotovo: (poruka: string) => void; onGreska: (g: string | null) => void;
}) {
  const [unos, setUnos] = useState<Record<string, string>>(() => Object.fromEntries(linkovi.map((l) => [l.offerId, l.url ?? ''])));
  const [radi, setRadi] = useState<string | null>(null);

  useEffect(() => { setUnos(Object.fromEntries(linkovi.map((l) => [l.offerId, l.url ?? '']))); }, [linkovi]);

  const sacuvaj = async (l: TenantLink) => {
    const url = (unos[l.offerId] ?? '').trim();
    setRadi(l.offerId);
    onGreska(null);
    try {
      await api.put(`/admin/tenants/${tenantId}/affiliate/links/${l.offerId}`, { url: url || null });
      onGotovo(url ? `Link za „${l.offerTitle}" je sačuvan.` : `Link za „${l.offerTitle}" je uklonjen - lokal više ne vidi tu ponudu.`);
    } catch (e) {
      onGreska(porukaGreske(e, 'Link nije sačuvan.'));
    } finally {
      setRadi(null);
    }
  };

  return (
    <div style={card}>
      <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, color: 'var(--color-text-strong)', display: 'flex', alignItems: 'center', gap: 6 }}>
        <Link2 size={16} /> Linkovi za ponude
      </h3>
      <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
        Za partnere bez potvrda: upišite link (ili link sa kodom) koji je partner dao baš za ovaj lokal. Partner izveštava
        po tom linku, pa se zna koliko je lokal zaradio. Bez linka lokal ponudu ne vidi. Samo https.
      </p>
      <div style={{ display: 'grid', gap: 10 }}>
        {linkovi.map((l) => {
          const vrednost = unos[l.offerId] ?? '';
          const promenjeno = vrednost.trim() !== (l.url ?? '');
          return (
            <div key={l.offerId} style={{ display: 'grid', gridTemplateColumns: 'minmax(160px, 1fr) 2.5fr auto', gap: 8, alignItems: 'center' }}>
              <div style={{ fontSize: 13 }}>
                <div style={{ fontWeight: 600, color: 'var(--color-text-strong)' }}>{l.offerTitle}</div>
                <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                  {l.networkName}{!l.offerActive && ' · ponuda ugašena'}{l.url ? ` · izmenjeno ${trenutak(l.updatedAt)}` : ' · nema linka'}
                </div>
              </div>
              <input
                value={vrednost}
                onChange={(e) => setUnos((u) => ({ ...u, [l.offerId]: e.target.value }))}
                disabled={!admin}
                placeholder="https://partner.example/?ref=ovaj-lokal"
                style={{ ...input, ...mono }}
                data-testid={`affiliate-link-${l.offerId}`}
              />
              {admin && (
                <button onClick={() => void sacuvaj(l)} disabled={!promenjeno || radi === l.offerId} style={{ ...btnSecondary, opacity: promenjeno ? 1 : 0.5 }}>
                  <Save size={16} /> {radi === l.offerId ? 'Čuvanje...' : vrednost.trim() ? 'Sačuvaj' : 'Ukloni'}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function danasIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Zarada koju partner nije javio potvrdom: provizija za ovaj lokal + referenca uplate partnera (D6). */
function DodajZaradu({ tenantId, nacin, onZatvori, onGotovo }: {
  tenantId: string; nacin: RewardMode; onZatvori: () => void; onGotovo: (poruka: string) => void;
}) {
  const [ponude, setPonude] = useState<OfferView[]>([]);
  const [offerId, setOfferId] = useState('');
  const [iznos, setIznos] = useState('');
  const [valuta, setValuta] = useState<Valuta>('RSD');
  const [dan, setDan] = useState(danasIso());
  const [referenca, setReferenca] = useState('');
  const [beleska, setBeleska] = useState('');
  const [greska, setGreska] = useState<string | null>(null);
  const [radi, setRadi] = useState(false);

  useEffect(() => {
    api.get<{ offers: OfferView[] }>('/admin/affiliate/offers')
      .then((r) => {
        // Prvo ponude sa linkom po lokalu - za njih se zarada i unosi ručno.
        const sve = [...r.offers].sort((a, b) => (a.kind === b.kind ? 0 : a.kind === 'tenant_link' ? -1 : 1));
        setPonude(sve);
        setOfferId((id) => id || sve[0]?.id || '');
      })
      .catch((e) => setGreska(porukaGreske(e, 'Ponude nisu učitane.')));
  }, []);

  const ponuda = ponude.find((p) => p.id === offerId);
  const normalizovano = iznos.trim().replace(',', '.');
  const iznosOk = /^\d{1,10}(\.\d{1,4})?$/.test(normalizovano) && Number(normalizovano) > 0;
  const spremno = !!offerId && iznosOk && /^\d{4}-\d{2}-\d{2}$/.test(dan) && !!referenca.trim();
  const procena = ponuda && iznosOk ? Math.floor(Number(normalizovano) * Number(ponuda.tenantSharePercent)) / 100 : null;

  const sacuvaj = async () => {
    if (!spremno) return;
    setRadi(true);
    setGreska(null);
    try {
      const r = await api.post<{ shareRsd: string; commissionRsd: string; rewardMode: RewardMode }>(`/admin/tenants/${tenantId}/affiliate/earnings`, {
        offerId, amount: normalizovano, currency: valuta, date: dan, paymentReference: referenca.trim(), note: beleska.trim() || undefined,
      });
      onGotovo(`Upisana zarada lokala ${rsd(r.shareRsd)} (provizija ${rsd(r.commissionRsd)}) - ${r.rewardMode === 'cash' ? 'raspoloživo za isplatu' : 'kredit za pretplatu'}.`);
    } catch (e) {
      setGreska(porukaGreske(e, 'Zarada nije upisana.'));
    } finally {
      setRadi(false);
    }
  };

  return (
    <Dijalog naslov="Dodaj zaradu" onZatvori={onZatvori} sirina={560}>
      {greska && <div style={errorBox}><AlertTriangle size={16} /> {greska}</div>}
      <Polje label="Ponuda" pomoc="Za koju ponudu je partner platio proviziju. Deo lokala je procenat te ponude.">
        <select value={offerId} onChange={(e) => setOfferId(e.target.value)} style={input} data-testid="zarada-ponuda">
          {ponude.map((p) => (
            <option key={p.id} value={p.id}>
              {p.title} · {p.tenantSharePercent} %{p.kind === 'tenant_link' ? ' · link po lokalu' : ''}{p.isActive ? '' : ' (ugašena)'}
            </option>
          ))}
        </select>
      </Polje>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.4fr', gap: 10 }}>
        <Polje label="Provizija od partnera" pomoc="Koliko je partner platio Efikasi za ovaj lokal (ne deo lokala).">
          <input
            value={iznos}
            onChange={(e) => setIznos(e.target.value)}
            inputMode="decimal"
            placeholder="npr. 25,40"
            style={{ ...input, borderColor: iznos && !iznosOk ? '#ef4444' : 'var(--color-border)' }}
            data-testid="zarada-iznos"
          />
        </Polje>
        <Polje label="Valuta">
          <select value={valuta} onChange={(e) => setValuta(e.target.value as Valuta)} style={input}>
            {VALUTE.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </Polje>
        <Polje label="Datum" pomoc="Za EUR/USD važi kurs NBS tog dana.">
          <input type="date" value={dan} onChange={(e) => setDan(e.target.value)} style={input} />
        </Polje>
      </div>
      <Polje label="Referenca uplate partnera (obavezno)" pomoc="Broj izvoda ili uplate kojom je partner platio. Zarada se upisuje samo za novac koji je stigao.">
        <input value={referenca} onChange={(e) => setReferenca(e.target.value)} maxLength={200} style={input} data-testid="zarada-referenca" />
      </Polje>
      <Polje label="Napomena (nije obavezno)" pomoc={'Npr. period izveštaja partnera: „septembar 2026".'}>
        <input value={beleska} onChange={(e) => setBeleska(e.target.value)} maxLength={256} style={input} />
      </Polje>
      <div style={napomena}>
        {procena !== null && ponuda
          ? <>Lokal dobija {ponuda.tenantSharePercent} % = <strong>{procena.toLocaleString('sr-RS', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {valuta}</strong>{valuta !== 'RSD' && ' (preračun u dinare po kursu NBS)'}, </>
          : 'Lokal dobija procenat ponude od unete provizije, '}
        kao {nacin === 'cash' ? 'novac raspoloživ za isplatu' : 'kredit za pretplatu'} (način nagrade ove firme).
        Upis se ne briše - greška se ispravlja poništenjem u tabeli prodaja.
      </div>
      <DugmadDijaloga onOtkazi={onZatvori} onPotvrdi={() => void sacuvaj()} potvrdi="Upiši zaradu" radi={radi} onemoguceno={!spremno} />
    </Dijalog>
  );
}

/** Isplata lokalu izvršena van sistema - ne sme preko raspoloživog (D6). */
function UpisiIsplatu({ tenantId, raspolozivo, onZatvori, onGotovo }: {
  tenantId: string; raspolozivo: string; onZatvori: () => void; onGotovo: (poruka: string) => void;
}) {
  const [iznos, setIznos] = useState('');
  const [referenca, setReferenca] = useState('');
  const [beleska, setBeleska] = useState('');
  const [greska, setGreska] = useState<string | null>(null);
  const [radi, setRadi] = useState(false);

  const normalizovano = iznos.trim().replace(',', '.');
  const broj = Number(normalizovano);
  const max = Number(raspolozivo);
  const iznosOk = /^\d+(\.\d{1,2})?$/.test(normalizovano) && broj > 0;
  const preko = iznosOk && broj > max;
  const spremno = iznosOk && !preko && !!referenca.trim();

  const sacuvaj = async () => {
    if (!spremno) return;
    setRadi(true);
    setGreska(null);
    try {
      await api.post(`/admin/tenants/${tenantId}/affiliate/manual-payouts`, {
        amountRsd: normalizovano, paymentReference: referenca.trim(), note: beleska.trim() || undefined,
      });
      onGotovo(`Upisana isplata ${rsd(normalizovano)}. Lokal je vidi u svojim isplatama.`);
    } catch (e) {
      setGreska(porukaGreske(e, 'Isplata nije upisana.'));
    } finally {
      setRadi(false);
    }
  };

  return (
    <Dijalog naslov="Upiši isplatu van sistema" onZatvori={onZatvori} sirina={480}>
      {greska && <div style={errorBox}><AlertTriangle size={16} /> {greska}</div>}
      <div style={{ ...napomena, marginBottom: 12 }}>
        Raspoloživo za isplatu: <strong style={{ color: 'var(--color-text-strong)' }}>{rsd(raspolozivo)}</strong>. Više od toga
        se ne može upisati - lokal sme da dobije samo novac koji je partner platio Efikasi.
      </div>
      <Polje label="Iznos (RSD)">
        <input
          value={iznos}
          onChange={(e) => setIznos(e.target.value)}
          inputMode="decimal"
          style={{ ...input, borderColor: iznos && (!iznosOk || preko) ? '#ef4444' : 'var(--color-border)' }}
          data-testid="isplata-iznos"
        />
        {preko && <div style={{ fontSize: 12, color: '#fca5a5', marginTop: 4 }}>Iznos je veći od raspoloživog.</div>}
      </Polje>
      <Polje label="Referenca uplate (obavezno)" pomoc="Broj naloga iz banke ili potvrde o isplati.">
        <input value={referenca} onChange={(e) => setReferenca(e.target.value)} maxLength={200} style={input} data-testid="isplata-referenca" />
      </Polje>
      <Polje label="Napomena (nije obavezno)">
        <input value={beleska} onChange={(e) => setBeleska(e.target.value)} maxLength={300} style={input} />
      </Polje>
      <div style={napomena}>Upisuje se kao isplaćen zahtev; lokal dobija obaveštenje. Upis se ne može poništiti.</div>
      <DugmadDijaloga onOtkazi={onZatvori} onPotvrdi={() => void sacuvaj()} potvrdi="Upiši isplatu" radi={radi} onemoguceno={!spremno} />
    </Dijalog>
  );
}
